import React, { useState, useCallback, useEffect } from 'react';
import { Box, Typography, Tooltip, Modal, Button, Slider, IconButton } from '@mui/material';
import { InfoOutlinedIcon, SlideshowOutlinedIcon, GifBoxOutlinedIcon, InsertPhotoOutlinedIcon, CloseIcon } from 'icons/materialIcons';
import Cropper from 'react-easy-crop';
import getCroppedImg from 'utils/images/cropImageUtils';
import { useEmailSignatureContext } from 'contexts/EmailSignatureContext';
import { useParams } from 'react-router-dom';
import { getCardById } from 'apis/cardApi';

const EmailSignatureBannerimage = () => {
  const [image, setImage] = useState(null);
  const [open, setOpen] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const [objectUrl, setObjectUrl] = useState(null);
  const { emailSignatureState, setEmailSignatureBannerImageUrl } = useEmailSignatureContext();
  const { cardId } = useParams();

  const calculateInitialZoom = useCallback(() => {
    return 1;
  }, []);

  const onMediaLoaded = useCallback(() => {
    setZoom(1);
    setCrop({ x: 0, y: 0 });
  }, []);

  const handleImageChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      const file = URL.createObjectURL(event.target.files[0]);
      setObjectUrl(file);
      setImage(file);
      setOpen(true);
      event.target.value = '';
    }
  };

  const handleClose = () => {
    setOpen(false);
    setImage(null);
    setObjectUrl(null);
    setZoom(1);
  };

  const handleRemoveImage = () => {
    setCroppedImage(null);
    setEmailSignatureBannerImageUrl(cardId, null);
    setImage(null);

    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
      setObjectUrl(null);
    }
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleSave = async () => {
    try {
      const croppedImageBlob = await getCroppedImg(image, croppedAreaPixels, 'rectangle', 'email-signature-banner-image');
      const croppedImageUrl = URL.createObjectURL(croppedImageBlob);
      setCroppedImage(croppedImageUrl);
      setEmailSignatureBannerImageUrl(cardId, croppedImageUrl);
      setOpen(false);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div>
      <Box
        sx={{
          width: '100%',
          height: '200px',
          margin: '20px auto',
          position: 'relative',
        }}
      >
        <Box
          component={!croppedImage ? 'label' : 'div'}
          sx={{
            width: '100%',
            height: '100%',
            borderRadius: '12px',
            backgroundColor: '#f2f2f2',
            border: '1px solid #dbdbdb',
            cursor: croppedImage ? 'default' : 'pointer',
            gap: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            textAlign: 'center',
            padding: "2",
            backgroundImage: croppedImage ? `url(${croppedImage})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          {!croppedImage && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                <InsertPhotoOutlinedIcon sx={{ fontSize: 40, color: '#cccccc', marginBottom: '7px' }} />
              </Box>
              <Typography
                variant="body2"
                sx={{ fontSize: '0.7rem', fontWeight: 400, color: '#212121' }}
              >
                <span style={{ color: '#2aaff7' }}>Select</span> file or drag and drop one here
              </Typography>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                style={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  top: 0,
                  left: 0,
                  opacity: 0,
                  cursor: 'pointer',
                }}
              />
            </>
          )}

          {croppedImage && (
            <IconButton
              onClick={handleRemoveImage}
              sx={{
                position: 'absolute',
                top: -7,
                right: -13,
                color: '#828282',
                backgroundColor: 'rgb(255, 255, 255)',
                '&:hover': { backgroundColor: 'rgb(255, 255, 255)' },
                width: 26,
                height: 26,
                boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                zIndex: 2,
              }}
            >
              <CloseIcon sx={{ fontSize: 12 }} />
            </IconButton>
          )}
        </Box>
      </Box>

      <Modal
        open={open}
        onClose={handleClose}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backdropFilter: 'blur(7px)',
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
        }}
      >
        <Box
          sx={{
            outline: 'none',
            width: '90vw', // Make modal take up more width
            maxWidth: '900px', // Limit max width for responsiveness
            height: '90vh', // Take more height for a bigger editor
            maxHeight: '700px', // Limit max height
          }}
        >
          {image && (
            <>
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: 'calc(100% - 100px)',
                  marginBottom: 2,
                }}
              >
                <Cropper
                  image={image}
                  crop={crop}
                  zoom={zoom}
                  aspect={2.6}
                  cropShape="rect"
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                  onMediaLoaded={onMediaLoaded}
                  objectFit="contain"
                  showGrid={false}
                  minZoom={0.05}
                  maxZoom={3}
                  restrictPosition={false}
                />
              </Box>

              <Box sx={{ textAlign: 'center' }}>

                <Slider
                  value={zoom}
                  onChange={(e, newValue) => setZoom(newValue)}
                  min={0.05}
                  max={3}
                  step={0.01}
                  defaultValue={1.5}
                  valueLabelDisplay="auto"
                  sx={{ marginBottom: 2 }}
                />

                <Button onClick={handleClose} variant="contained" sx={{ marginRight: 2 }}>
                  Cancel
                </Button>
                <Button onClick={handleSave} variant="contained" color="primary">
                  Crop
                </Button>


              </Box>
            </>
          )}
        </Box>
      </Modal>
    </div>
  );
};

export default EmailSignatureBannerimage;